import { Container, Row, Col, Card, CardBody, Image, Spinner, Badge } from 'react-bootstrap';
import { BsEnvelopeFill, BsTelephoneFill, BsPerson } from 'react-icons/bs';
import { useAboutPage, getAboutSectionByKey, iconMap } from '@/hooks/useAboutPage';
import { getAboutImage } from '@/utils/imageUrl';

const TeamMembers = ({ sectionData: propSectionData }) => {
  const { sections, loading } = useAboutPage();
  const teamSection = propSectionData || getAboutSectionByKey(sections, 'team_members');
  
  const bgColor = teamSection?.background_color || 'white';
  const bgEffect = teamSection?.background_effect || null;
  let bgClass = bgColor === 'primary' ? 'section-bg-primary' : bgColor === 'white' ? 'bg-white' : 'bg-light';
  // Apply gradient effect if selected
  if (bgColor === 'primary' && bgEffect === 'gradient') {
    bgClass = 'section-bg-primary-gradient';
  }
  
  if (loading && !propSectionData) {
    return (
      <section className={`py-4 py-lg-5 ${bgClass} about-section team-members-section`}>
        <Container>
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        </Container>
      </section>
    );
  }
  
  const teamMembers = teamSection?.items || [];
  const hasDotsEffect = bgColor === 'primary' && bgEffect === 'dots';
  
  if (!teamSection || teamMembers.length === 0) {
    return null;
  }
  
  return (
    <section className={`py-4 py-lg-5 ${bgClass} about-section team-members-section ${hasDotsEffect ? 'position-relative overflow-hidden' : ''}`}>
      {hasDotsEffect && (
        <>
          <span className="section-bg-dots section-bg-dots--left d-none d-xl-block" />
          <span className="section-bg-dots section-bg-dots--right d-none d-lg-block" />
        </>
      )}
      <Container className={hasDotsEffect ? 'position-relative' : ''}>
        {/* Header Section */}
        <Row className="mb-4">
          <Col>
            {teamSection.subtitle && (
              <Badge bg="primary" className="bg-opacity-10 text-primary mb-3 px-3 py-2">
                {teamSection.subtitle}
              </Badge>
            )}
            {teamSection.title && (
              <h2 className="sec-title mb-3">{teamSection.title}</h2>
            )}
            {teamSection.intro_text && (
              <p className="lead intro-text mb-0" dangerouslySetInnerHTML={{ __html: teamSection.intro_text }} />
            )}
          </Col>
        </Row>
        <Row className="g-4">
          {teamMembers.map((member) => {
            const name = member.description || '';
            const position = member.title || '';
            const rawContent = (() => {
              if (!member?.content) return {};
              if (typeof member.content === 'string') {
                try {
                  return JSON.parse(member.content);
                } catch {
                  return {};
                }
              }
              return member.content || {};
            })();
            const email = rawContent?.email || '';
            const phone = rawContent?.phone || '';
            const options = Array.isArray(rawContent?.options) ? rawContent.options : [];
            const imageUrl = getAboutImage(member.image);
            
            return (
              <Col key={member.id} sm={6} md={4} lg={3}>
                <Card className="team-member-card h-100 border-0">
                  <div className={`team-member-image-wrapper position-relative overflow-hidden ${!imageUrl ? 'team-member-image-wrapper--placeholder' : ''}`}>
                    {imageUrl ? (
                      <Image 
                        src={imageUrl} 
                        className="team-member-image"
                        alt={name}
                      />
                    ) : (
                      <div className="team-member-image-placeholder">
                        <BsPerson className="team-member-placeholder-icon" />
                      </div>
                    )}
                  </div>
                  <CardBody className="team-member-body text-center">
                    <h5 className="team-member-name mb-1">{name}</h5>
                    <p className="team-member-position mb-3">{position}</p>
                    {(phone || email || options.length > 0) && (
                      <div className="team-member-contact">
                        {phone && (
                          <div className="team-member-contact-item">
                            <BsTelephoneFill className="team-member-contact-icon" />
                            <span className="team-member-contact-text">{phone}</span>
                          </div>
                        )}
                        {email && (
                          <div className="team-member-contact-item">
                            <BsEnvelopeFill className="team-member-contact-icon" />
                            <a href={`mailto:${email}`} className="team-member-contact-link">
                              {email}
                            </a>
                          </div>
                        )}
                        {options.length > 0 && (
                          <div className="team-member-options">
                            {options.map((option, idx) => {
                              const Icon = option?.icon ? iconMap[option.icon] : null;
                              if (!option?.text) return null;
                              return (
                                <div key={`${member.id}-option-${idx}`} className="team-member-contact-item">
                                  {Icon ? <Icon className="team-member-contact-icon" /> : null}
                                  <span className="team-member-contact-text">{option.text}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Container>
    </section>
  );
};

export default TeamMembers;
